import React, { useState, useEffect, useMemo } from 'react';
//import axios from 'axios';
import {
  Box,
  Button,
  Popover,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Chip,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getAnswerOptionsLibrary } from '../../../../../../../services/data-entry/templates.service';
import './TemplateSelector.css';

const TemplateSelector = ({
  templateType,
  questionType,
  questionVariant,
  onSelectTemplate,
  selectedTemplate
}) => {
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch templates from API
  useEffect(() => {
    const fetchTemplates = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getAnswerOptionsLibrary(templateType);

        if (response.data && Array.isArray(response.data)) {
          setTemplates(response.data);
        }
      } catch (err) {
        setError('Failed to load templates');
        console.error('Error fetching templates:', err);
      } finally {
        setLoading(false);
      }
    };

    if (templateType) {
      fetchTemplates();
    }
  }, [templateType]);

  // Filter templates based on question type and variant
  useEffect(() => {
    if (templates.length === 0) return;

    let filtered = templates;

    // Filter by question type
    if (questionType) {
      filtered = filtered.filter((template) => template.type?.value === questionType);
    }

    // Filter by variant if applicable
    if (questionType !== 'ranking' && questionVariant) {
      filtered = filtered.filter((template) => {
        // If template has no variants array, skip it
        if (!template.variants || !Array.isArray(template.variants) || template.variants.length === 0) {
          return false;
        }

        // Check if any variant in the array matches the question variant
        return template.variants.some((v) => v?.value === questionVariant);
      });
    }

    setFilteredTemplates(filtered);
  }, [templates, questionType, questionVariant]);

  // Helper function to get group icon (move before useMemo)
  const getGroupIcon = (groupKey) => {
    const iconMap = {
      rating: 'star',
      star: 'star',
      frequency: 'clock',
      yes_no: 'check-circle',
      agreement: 'handshake',
      satisfaction: 'smile',
      importance: 'exclamation-circle',
      quality: 'award',
      likelihood: 'percentage',
      confidence: 'shield-alt',
      difficulty: 'signal',
      comparison: 'balance-scale',
      preference: 'heart',
      year_levels: 'graduation-cap',
      demographics: 'users'
    };

    for (const [key, icon] of Object.entries(iconMap)) {
      if (groupKey.includes(key)) return icon;
    }
    return 'list';
  };

  // Filter templates by search query and group them
  const searchedAndGroupedTemplates = useMemo(() => {
    let searched = filteredTemplates;

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      searched = filteredTemplates.filter((template) => {
        const titleMatch = template.title?.toLowerCase().includes(query);
        const groupMatch = template.group?.title?.toLowerCase().includes(query);
        const optionsMatch = template.options?.some((opt) => opt.option?.toLowerCase().includes(query));
        return titleMatch || groupMatch || optionsMatch;
      });
    }

    // Group filtered templates by their group key
    const grouped = searched.reduce((acc, template) => {
      const groupKey = template.group?.key || 'ungrouped';
      const groupTitle = template.group?.title || 'Other';

      if (!acc[groupKey]) {
        acc[groupKey] = {
          title: groupTitle,
          templates: [],
          icon: getGroupIcon(groupKey)
        };
      }

      acc[groupKey].templates.push(template);
      return acc;
    }, {});

    return grouped;
  }, [filteredTemplates, searchQuery]);

  const hasGroups = filteredTemplates.length > 0;

  // Auto-expand groups when searching or when first loaded
  useEffect(() => {
    if (searchQuery.trim()) {
      const allGroups = Object.keys(searchedAndGroupedTemplates).reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {});
      setExpandedGroups(allGroups);
    } else if (
      Object.keys(searchedAndGroupedTemplates).length > 0 &&
      Object.keys(expandedGroups).length === 0
    ) {
      setExpandedGroups({ [Object.keys(searchedAndGroupedTemplates)[0]]: true });
    }
  }, [searchQuery, searchedAndGroupedTemplates]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchQuery('');
  };

  const handleGroupClick = (groupKey) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };

  const handleTemplateSelect = (template) => {
    onSelectTemplate(template);
    handleClose();
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const totalResults = Object.values(searchedAndGroupedTemplates).reduce(
    (sum, group) => sum + group.templates.length,
    0
  );

  const open = Boolean(anchorEl);
  const id = open ? 'template-selector-popover' : undefined;

  return (
    <>
      {hasGroups ? (
        <>
          {' '}
          <Box className='template-selector'>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <FontAwesomeIcon icon='sparkles' style={{ fontSize: '16px', color: '#2c5f7c' }} />
              <Typography variant='subtitle2' sx={{ fontWeight: 600, color: '#2c5f7c', fontSize: '14px' }}>
                Load from template
              </Typography>
              <Chip
                label='Optional'
                size='small'
                variant='outlined'
                sx={{ height: '20px', fontSize: '12px' }}
              />
            </Box>

            <Button
              aria-describedby={id}
              variant='outlined'
              onClick={handleClick}
              disabled={loading || filteredTemplates.length === 0}
              fullWidth
              sx={{
                textTransform: 'none',
                justifyContent: 'space-between',
                py: 1.5,
                px: 2,
                borderRadius: 2,
                borderColor: selectedTemplate ? '#2c5f7c' : '#d0d0d0',
                backgroundColor: selectedTemplate ? '#f0f7ff' : 'white',
                '&:hover': {
                  borderColor: '#2c5f7c',
                  backgroundColor: '#f8f9fa'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
                {selectedTemplate ? (
                  <>
                    <FontAwesomeIcon icon='check-circle' style={{ color: '#2c5f7c', fontSize: '16px' }} />
                    <Typography sx={{ fontSize: '12px', fontWeight: 500, textAlign: 'left', flex: 1 }}>
                      {selectedTemplate.title}
                    </Typography>
                  </>
                ) : (
                  <Typography sx={{ fontSize: '12px', color: '#666', textAlign: 'left' }}>
                    Select a template to auto-fill answer options below
                  </Typography>
                )}
              </Box>
              <FontAwesomeIcon icon='chevron-down' style={{ fontSize: '12px', color: '#666' }} />
            </Button>

            <Popover
              id={id}
              open={open}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left'
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left'
              }}
              PaperProps={{
                sx: {
                  width: 520,
                  maxHeight: 600,
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  mt: 1
                }
              }}
            >
              {loading ? (
                <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <CircularProgress size={32} />
                  <Typography variant='body2' color='text.secondary'>
                    Loading templates...
                  </Typography>
                </Box>
              ) : error ? (
                <Box sx={{ p: 2 }}>
                  <Alert severity='error'>{error}</Alert>
                </Box>
              ) : (
                <>
                  {/* Header with Search */}
                  <Box
                    sx={{
                      p: 2.5,
                      borderBottom: '1px solid #e0e0e0',
                      backgroundColor: '#fafafa'
                    }}
                  >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}
                    >
                      <Typography variant='h6' sx={{ fontSize: '14px', fontWeight: 600 }}>
                        Choose Template
                      </Typography>
                      <Tooltip title='Close'>
                        <IconButton size='small' onClick={handleClose} sx={{ ml: 1 }}>
                          <FontAwesomeIcon icon='times' style={{ fontSize: '14px' }} />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                        mb: 2,
                        p: 1.5,
                        backgroundColor: '#fff8e1',
                        borderRadius: 1,
                        borderLeft: '3px solid #ffa726'
                      }}
                    >
                      <FontAwesomeIcon
                        icon='info-circle'
                        style={{ fontSize: '14px', color: '#f57c00', marginTop: '2px' }}
                      />
                      <Typography
                        variant='caption'
                        sx={{
                          fontSize: '0.95rem',
                          color: '#e65100',
                          lineHeight: 1.4
                        }}
                      >
                        Selecting a template will replace any existing answer options in your question.
                      </Typography>
                    </Box>

                    <TextField
                      fullWidth
                      size='small'
                      placeholder='Search by name or options...'
                      value={searchQuery}
                      onChange={handleSearchChange}
                      autoFocus
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position='start'>
                            <FontAwesomeIcon icon='search' style={{ fontSize: '14px', color: '#999' }} />
                          </InputAdornment>
                        ),
                        endAdornment: searchQuery && (
                          <InputAdornment position='end'>
                            <IconButton size='small' onClick={() => setSearchQuery('')} sx={{ p: 0.5 }}>
                              <FontAwesomeIcon
                                icon='times-circle'
                                style={{ fontSize: '14px', color: '#999' }}
                              />
                            </IconButton>
                          </InputAdornment>
                        ),
                        sx: {
                          borderRadius: 1.5,
                          backgroundColor: 'white',
                          '& fieldset': {
                            borderColor: '#d0d0d0'
                          }
                        }
                      }}
                    />

                    {searchQuery && (
                      <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={`${totalResults} result${totalResults !== 1 ? 's' : ''}`}
                          size='small'
                          color='primary'
                          variant='outlined'
                          sx={{ fontSize: '0.75rem' }}
                        />
                      </Box>
                    )}
                  </Box>

                  {/* Templates List */}
                  <Box sx={{ flex: 1, overflow: 'auto' }}>
                    {filteredTemplates.length === 0 ? (
                      <Box sx={{ p: 4, textAlign: 'center' }}>
                        <FontAwesomeIcon
                          icon='inbox'
                          style={{ fontSize: '48px', color: '#ccc', marginBottom: '16px' }}
                        />
                        <Typography variant='body2' color='text.secondary'>
                          No templates available for this question type
                        </Typography>
                      </Box>
                    ) : Object.keys(searchedAndGroupedTemplates).length === 0 ? (
                      <Box sx={{ p: 4, textAlign: 'center' }}>
                        <FontAwesomeIcon
                          icon='search'
                          style={{ fontSize: '48px', color: '#ccc', marginBottom: '16px' }}
                        />
                        <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                          No templates match your search
                        </Typography>
                        <Button size='small' onClick={() => setSearchQuery('')} variant='outlined'>
                          Clear Search
                        </Button>
                      </Box>
                    ) : (
                      <List sx={{ p: 0 }}>
                        {Object.entries(searchedAndGroupedTemplates).map(([groupKey, group], index) => (
                          <React.Fragment key={groupKey}>
                            {index > 0 && <Divider />}

                            <ListItemButton
                              onClick={() => handleGroupClick(groupKey)}
                              sx={{
                                py: 1.5,
                                px: 2.5,
                                backgroundColor: '#f8f9fa',
                                '&:hover': {
                                  backgroundColor: '#e9ecef'
                                }
                              }}
                            >
                              <Box
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: '8px',
                                  backgroundColor: expandedGroups[groupKey] ? '#32668b' : '#cfd3dc',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  mr: 1.5,
                                  transition: 'all 0.2s'
                                }}
                              >
                                <FontAwesomeIcon
                                  icon={group.icon}
                                  style={{
                                    fontSize: '14px',
                                    color: expandedGroups[groupKey] ? '#fff' : '#333'
                                  }}
                                />
                              </Box>
                              <ListItemText
                                primary={group.title}
                                secondary={`${group.templates.length} template${group.templates.length !== 1 ? 's' : ''}`}
                                primaryTypographyProps={{
                                  fontWeight: 600,
                                  fontSize: '14px'
                                }}
                                secondaryTypographyProps={{
                                  fontSize: '12px'
                                }}
                              />
                              <FontAwesomeIcon
                                icon={expandedGroups[groupKey] ? 'chevron-up' : 'chevron-down'}
                                style={{ fontSize: '12px', color: '#666' }}
                              />
                            </ListItemButton>

                            <Collapse in={expandedGroups[groupKey]} timeout='auto' unmountOnExit>
                              <List component='div' disablePadding sx={{ backgroundColor: '#fff' }}>
                                {group.templates.map((template, idx) => (
                                  <React.Fragment key={template._id}>
                                    <ListItemButton
                                      onClick={() => handleTemplateSelect(template)}
                                      selected={selectedTemplate?._id === template._id}
                                      sx={{
                                        pl: 5.5,
                                        pr: 2.5,
                                        py: 1.5,
                                        borderLeft:
                                          selectedTemplate?._id === template._id
                                            ? '3px solid #1976d2'
                                            : '3px solid transparent',
                                        '&.Mui-selected': {
                                          backgroundColor: '#e3f2fd',
                                          '&:hover': {
                                            backgroundColor: '#bbdefb'
                                          }
                                        },
                                        '&:hover': {
                                          backgroundColor: '#f5f5f5'
                                        }
                                      }}
                                    >
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                          {selectedTemplate?._id === template._id && (
                                            <FontAwesomeIcon
                                              icon='check-circle'
                                              style={{ color: '#32668b', fontSize: '14px' }}
                                            />
                                          )}
                                          <Typography
                                            sx={{
                                              fontSize: '12px',
                                              fontWeight: selectedTemplate?._id === template._id ? 600 : 500,
                                              color:
                                                selectedTemplate?._id === template._id
                                                  ? '#32668b'
                                                  : 'inherit',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              whiteSpace: 'nowrap'
                                            }}
                                          >
                                            {template.title}
                                          </Typography>
                                        </Box>
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            flexWrap: 'wrap'
                                          }}
                                        >
                                          <Chip
                                            label={`${template.options.length} options`}
                                            size='small'
                                            sx={{
                                              height: '20px',
                                              fontSize: '12px',
                                              backgroundColor: '#f5f5f5',
                                              color: '#666'
                                            }}
                                          />
                                          {template.options.length > 0 && (
                                            <Typography
                                              variant='caption'
                                              sx={{
                                                color: '#999',
                                                fontSize: '11px',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                maxWidth: '300px'
                                              }}
                                            >
                                              {template.options
                                                .slice(0, 3)
                                                .map((opt) => opt.option)
                                                .join(', ')}
                                              {template.options.length > 3 && '...'}
                                            </Typography>
                                          )}
                                        </Box>
                                      </Box>
                                    </ListItemButton>
                                    {idx < group.templates.length - 1 && (
                                      <Divider variant='inset' component='li' sx={{ ml: 5.5 }} />
                                    )}
                                  </React.Fragment>
                                ))}
                              </List>
                            </Collapse>
                          </React.Fragment>
                        ))}
                      </List>
                    )}
                  </Box>
                </>
              )}
            </Popover>
          </Box>
        </>
      ) : (
        <></>
      )}
    </>
  );
};

export default TemplateSelector;
